import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TrendingUp, Info, BarChart3 } from 'lucide-react';
import Seo from '@/components/Seo';

const formatCr = (n) => {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
};

const RentalYieldCalculatorPage = () => {
  const [sp] = useSearchParams();
  const [propertyValue, setPropertyValue] = useState(Number(sp.get('value')) || 8000000);
  const [monthlyRent, setMonthlyRent] = useState(Number(sp.get('rent')) || 35000);
  const [maintenance, setMaintenance] = useState(5000);
  const [propertyTax, setPropertyTax] = useState(15000); // annual
  const [appreciation, setAppreciation] = useState(6); // % annual
  const [years, setYears] = useState(5);

  const r = useMemo(() => {
    const annualRent = monthlyRent * 12;
    const annualMaintenance = maintenance * 12;
    const netAnnualIncome = annualRent - annualMaintenance - propertyTax;
    const grossYield = (annualRent / propertyValue) * 100;
    const netYield = (netAnnualIncome / propertyValue) * 100;
    const futureValue = propertyValue * Math.pow(1 + appreciation / 100, years);
    const totalRentEarned = netAnnualIncome * years;
    const totalReturn = (futureValue - propertyValue) + totalRentEarned;
    const roiPercent = (totalReturn / propertyValue) * 100;
    return { annualRent, annualMaintenance, netAnnualIncome, grossYield, netYield, futureValue, totalRentEarned, totalReturn, roiPercent };
  }, [propertyValue, monthlyRent, maintenance, propertyTax, appreciation, years]);

  const rating = r.netYield >= 4 ? 'Excellent' : r.netYield >= 3 ? 'Good' : r.netYield >= 2 ? 'Fair' : 'Below average';
  const ratingColor = r.netYield >= 4 ? 'text-green-600 bg-green-50' : r.netYield >= 3 ? 'text-blue-600 bg-blue-50' : r.netYield >= 2 ? 'text-amber-600 bg-amber-50' : 'text-red-600 bg-red-50';

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-16" data-testid="rental-yield-page">
      <Seo title="Rental Yield & ROI Calculator" description="Estimate your real estate investment returns from rental income and capital appreciation. Get gross yield, net yield, and 5-year ROI." path="/rental-yield-calculator" />
      <div className="container-luxury max-w-5xl">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] rounded-full text-xs font-medium mb-3">
            <TrendingUp className="h-3.5 w-3.5" /> Investment ROI tool
          </div>
          <h1 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold text-[#0F172A] mb-3">
            Rental Yield &amp; ROI Calculator
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Estimate your real estate investment returns from rental income + capital appreciation.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white p-8 rounded-sm shadow-sm border border-border/50">
            <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold text-[#0F172A] mb-6 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-[#D4AF37]" /> Investment Inputs
            </h2>
            <div className="space-y-5">
              <YField label="Property purchase value" value={propertyValue} setValue={setPropertyValue} prefix="₹" testid="value" hint={formatCr(propertyValue)} />
              <YField label="Monthly rent" value={monthlyRent} setValue={setMonthlyRent} prefix="₹" testid="rent" />
              <YField label="Monthly maintenance" value={maintenance} setValue={setMaintenance} prefix="₹" testid="maintenance" hint="Society charges, repairs etc." />
              <YField label="Annual property tax" value={propertyTax} setValue={setPropertyTax} prefix="₹" testid="tax" />
              <div className="grid grid-cols-2 gap-4">
                <YField label="Appreciation rate" value={appreciation} setValue={setAppreciation} suffix="%" testid="appreciation" step="0.1" />
                <YField label="Holding period" value={years} setValue={setYears} suffix="yrs" testid="years" />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-[#0F172A] text-white p-8 rounded-sm shadow-md" data-testid="rental-yield-result">
              <div className="flex items-start justify-between mb-2">
                <p className="text-white/60 text-xs uppercase tracking-wider">Net rental yield</p>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${ratingColor}`}>{rating}</span>
              </div>
              <p className="font-['Plus_Jakarta_Sans'] text-5xl md:text-6xl font-bold text-[#D4AF37] mb-1">
                {r.netYield.toFixed(2)}%
              </p>
              <p className="text-white/70 text-sm">
                Gross yield: <strong className="text-white">{r.grossYield.toFixed(2)}%</strong> · Industry avg in India: 2–4%
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Stat label="Net annual income" value={formatCr(r.netAnnualIncome)} sub="rent − costs" />
              <Stat label="Annual rent" value={formatCr(r.annualRent)} sub="gross" />
              <Stat label={`Property value in ${years}y`} value={formatCr(r.futureValue)} sub={`@${appreciation}% p.a.`} />
              <Stat label={`Total ${years}y return`} value={formatCr(r.totalReturn)} sub={`${r.roiPercent.toFixed(1)}% ROI`} highlight />
            </div>

            <div className="bg-amber-50 border border-amber-200 p-4 rounded-sm flex gap-3 text-sm text-amber-900">
              <Info className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p>
                <strong>Pro tip:</strong> A net yield above 3% in metro cities is considered very good. Don't forget to factor in vacancy periods (typically 1 month/year) and tax on rental income.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const YField = ({ label, value, setValue, prefix, suffix, testid, hint, step }) => (
  <div>
    <Label className="text-sm">{label}</Label>
    <div className="relative mt-1">
      {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">{prefix}</span>}
      <Input
        type="number"
        step={step || '1'}
        value={value}
        onChange={(e) => setValue(Number(e.target.value) || 0)}
        className={`${prefix ? 'pl-7' : ''} ${suffix ? 'pr-12' : ''}`}
        data-testid={`yield-${testid}`}
      />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">{suffix}</span>}
    </div>
    {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
  </div>
);

const Stat = ({ label, value, sub, highlight }) => (
  <div className={`p-4 rounded-sm border ${highlight ? 'bg-[#D4AF37]/10 border-[#D4AF37]/30' : 'bg-white border-border/50'}`}>
    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{label}</p>
    <p className={`font-bold text-xl ${highlight ? 'text-[#D4AF37]' : 'text-[#0F172A]'}`}>{value}</p>
    <p className="text-xs text-muted-foreground">{sub}</p>
  </div>
);

export default RentalYieldCalculatorPage;
